import React, { useState, useEffect } from 'react';
import { User } from 'firebase/auth';
import { onAuthStateChange, signOutUser } from '../lib/auth';
import {
  UserCircleIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  BellIcon,
  ShieldCheckIcon,
  KeyIcon,
  EnvelopeIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';

interface UserProfileProps {
  collapsed?: boolean;
  position?: 'top' | 'bottom'; // 控制下拉選單位置
}

const UserProfile: React.FC<UserProfileProps> = ({ collapsed = false, position = 'bottom' }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    // 監聽身份驗證狀態變化
    const unsubscribe = onAuthStateChange((user) => {
      setUser(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (user) {
      setDisplayName(user.displayName || '');
      setEmail(user.email || '');
    }
  }, [user]);

  // 點擊外部關閉選單
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target && !target.closest('.user-profile-menu')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleLogout = async () => {
    try {
      await signOutUser();
      setIsOpen(false);
    } catch {
      // 登出失敗，靜默處理
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      // 注意：Firebase Auth 不允許直接更新 displayName 和 email
      // 這些通常需要重新驗證，所以我們只顯示訊息
      setMessage('個人資料更新功能需要重新驗證，請聯繫管理員');
      setEditMode(false);
    } catch {
      setMessage('更新失敗，請稍後再試');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center px-4 py-2">
        <div className="animate-pulse flex space-x-4">
          <div className="rounded-full bg-gray-200 h-8 w-8"></div>
          <div className="flex-1 space-y-2 py-1">
            <div className="h-4 bg-gray-200 rounded w-20"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="relative w-full">
        <div className="flex items-center space-x-3 w-full p-3 text-left">
          <div className="flex-shrink-0">
            <UserCircleIcon className="h-8 w-8 text-gray-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-700 truncate">未登入</p>
            <p className="text-xs text-gray-500 truncate">&nbsp;</p>
          </div>
        </div>
        <div className="px-3 pb-3">
          <button
            onClick={handleLogout}
            className="w-full flex items-center px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
          >
            <ArrowRightOnRectangleIcon className="mr-3 h-4 w-4" />
            登出
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative user-profile-menu">
      {/* 用戶資訊按鈕 */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center ${position === 'top' ? 'p-2' : 'w-full p-3'} text-left hover:bg-gray-50 rounded-lg transition-colors ${collapsed ? 'justify-center' : position === 'top' ? 'space-x-2' : 'space-x-3'}`}
        title={collapsed ? (user.displayName || user.email || '') : ''}
      >
        <div className="flex-shrink-0">
          {user.photoURL ? (
            <img
              className={`${position === 'top' ? 'h-8 w-8' : 'h-8 w-8'} rounded-full`}
              src={user.photoURL}
              alt="用戶頭像"
            />
          ) : (
            <UserCircleIcon className={`${position === 'top' ? 'h-8 w-8' : 'h-8 w-8'} text-gray-400`} />
          )}
        </div>
        {!collapsed && position !== 'top' && (
          <>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-700 truncate">
                {user.displayName || user.email}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {user.email}
              </p>
            </div>
            <Cog6ToothIcon className="h-4 w-4 text-gray-400" />
          </>
        )}
      </button>

      {/* 下拉選單 */}
      {isOpen && (
        <div className={`absolute ${position === 'top' ? 'top-full right-0 mt-2' : 'bottom-full left-0 right-0 mb-2'} bg-white rounded-lg shadow-lg border border-gray-200 ${position === 'top' ? 'z-[9999] min-w-[200px]' : 'z-50'}`}>
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              {user.photoURL ? (
                <img
                  className="h-10 w-10 rounded-full"
                  src={user.photoURL}
                  alt="用戶頭像"
                />
              ) : (
                <UserCircleIcon className="h-10 w-10 text-gray-400" />
              )}
              <div>
                {editMode ? (
                  <>
                    <input
                      className="text-sm font-medium text-gray-900 border rounded px-1 py-0.5 mb-1 w-32"
                      value={displayName}
                      onChange={e => setDisplayName(e.target.value)}
                      placeholder="姓名"
                      disabled={saving}
                    />
                    <input
                      className="text-xs text-gray-500 border rounded px-1 py-0.5 w-40"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="Email"
                      disabled={saving}
                    />
                  </>
                ) : (
                  <>
                    <p className="text-sm font-medium text-gray-900">
                      {user.displayName || '未設定姓名'}
                    </p>
                    <p className="text-xs text-gray-500">{user.email}</p>
                  </>
                )}
                <p className="text-xs text-gray-400">
                  註冊時間: {formatDate(new Date(user.metadata.creationTime || Date.now()))}
                </p>
              </div>
            </div>
            <div className="mt-2 flex gap-2">
              {editMode ? (
                <>
                  <button
                    className="btn-primary px-2 py-1 text-xs"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? '儲存中...' : '儲存'}
                  </button>
                  <button
                    className="btn-secondary px-2 py-1 text-xs"
                    onClick={() => { setEditMode(false); setMessage(''); }}
                    disabled={saving}
                  >
                    取消
                  </button>
                </>
              ) : (
                <button
                  className="btn-secondary px-2 py-1 text-xs"
                  onClick={() => setEditMode(true)}
                >
                  編輯
                </button>
              )}
              {message && <span className="text-xs text-green-600 ml-2">{message}</span>}
            </div>
          </div>

          <div className="p-2">
            {/* 設定選項 */}
            <div className="space-y-1">
              <button className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors">
                <EnvelopeIcon className="mr-3 h-4 w-4 text-gray-400" />
                通知設定
              </button>
              <button className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors">
                <BellIcon className="mr-3 h-4 w-4 text-gray-400" />
                系統通知
              </button>
              <button className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors">
                <ShieldCheckIcon className="mr-3 h-4 w-4 text-gray-400" />
                隱私設定
              </button>
              <button className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors">
                <KeyIcon className="mr-3 h-4 w-4 text-gray-400" />
                變更密碼
              </button>
              <button className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors">
                <CalendarIcon className="mr-3 h-4 w-4 text-gray-400" />
                活動記錄
              </button>
            </div>

            {/* 分隔線 */}
            <div className="border-t border-gray-200 my-2"></div>

            {/* 登出按鈕 */}
            <button
              onClick={handleLogout}
              className="w-full flex items-center px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
            >
              <ArrowRightOnRectangleIcon className="mr-3 h-4 w-4" />
              登出
            </button>
          </div>
        </div>
      )}

      {/* 點擊外部關閉選單 */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default UserProfile; 